/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { PaginatedTable } from '@ink-ai/portal/common/components/paginated-table/PaginatedTable';
import { useAsyncData } from '@ink-ai/portal/common/hooks/useAsyncData';
import { getApi } from '@ink-ai/portal/common/requestHelper';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import JSZip from 'jszip';
import { PaperCard } from '../components/Common';
import {
  GlossaryExtractionApi,
  GlossaryExtractionResponseItemDtoStatusEnum,
} from '@ink-ai/insight-service-sdk';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { EllipsisCell } from '@ink-ai/portal/common/components/paginated-table/EllipsisCell';
import { formatDate } from '@ink-ai/portal/common/utils';
import {
  handleDownload,
  languageOptions,
  statusClassMapping,
  statusStringMapping,
  getGlossaryExtractionFile,
} from '@ink-ai/portal/reducers/glossary-extraction';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { debounce } from 'lodash-es';
import { useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import { WsMessageType } from '@ink-ai/portal/common/socket/SocketHandler';
import LoadingButton from '@mui/lab/LoadingButton';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { DeleteOutlineOutlined, FileDownloadOutlined } from '@mui/icons-material';

export const statusIconMapping: Record<
  GlossaryExtractionResponseItemDtoStatusEnum,
  React.ReactNode
> = {
  [GlossaryExtractionResponseItemDtoStatusEnum.PendingUpload]: (
    <PendingOutlinedIcon />
  ),
  [GlossaryExtractionResponseItemDtoStatusEnum.Queueing]: (
    <PendingOutlinedIcon />
  ),
  [GlossaryExtractionResponseItemDtoStatusEnum.Extracting]: (
    <PendingOutlinedIcon />
  ),
  [GlossaryExtractionResponseItemDtoStatusEnum.Success]: (
    <CheckCircleOutlineOutlinedIcon />
  ),
  [GlossaryExtractionResponseItemDtoStatusEnum.Error]: (
    <ErrorOutlineOutlinedIcon />
  ),
};

export const ExtractGlossaryTaskList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [downloadId, setDownloadId] = useState('');
  const [importId, setImportId] = useState('');
  const [menuLoading, setMenuLoading] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(0);
  const [tempSearch, setTempSearch] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const webSocketState = useSelector((state: RootState) => state.webSocket);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const { data, isLoadingData, reloadData } = useAsyncData(async () => {
    const glossaryExtractionApi = await getApi(GlossaryExtractionApi);
    return glossaryExtractionApi.queryGlossaryExtraction({
      pagination: {
        take: rowsPerPage,
        skip: page * rowsPerPage,
      },
      search,
    });
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    if (lastSyncAt === webSocketState.lastMessageAt) {
      // already in syncing
      return;
    }
    setLastSyncAt(webSocketState.lastMessageAt);
    if (isLoadingData) {
      return;
    }
    if (
      webSocketState.lastMessage?.type !==
      WsMessageType.GLOSSARY_EXTRACTION_RESULT
    ) {
      return;
    }
    const updatedId = webSocketState.lastMessage?.payload.uuid;

    // reload data if current data contains task that to be updated;
    if (
      data.data.data.some(
        ({ id, status }) =>
          id === updatedId &&
          (status === 'EXTRACTING' || status === 'QUEUEING'),
      )
    ) {
      reloadData();
    }
  }, [
    webSocketState.lastMessage,
    webSocketState.lastMessageAt,
    data,
    reloadData,
    isLoadingData,
    lastSyncAt,
  ]);

  const deleteTasks = async (ids: string[]) => {
    if (!ids || ids.length === 0) {
      return;
    }
    const glossaryExtractionApi = await getApi(GlossaryExtractionApi);
    for (const id of ids) {
      await glossaryExtractionApi.deleteGlossaryExtraction(id);
    }
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0 || menuLoading) return;
    setPendingDeleteIds(selectedIds);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    const ids = pendingDeleteIds.length > 0 ? pendingDeleteIds : selectedIds;
    if (!ids || ids.length === 0) return;
    setMenuLoading(true);
    try {
      await deleteTasks(ids);
    } catch (error) {
      console.error(error);
    } finally {
      setMenuLoading(false);
      setConfirmDeleteOpen(false);
      setPendingDeleteIds([]);
    }
    handleMenuClose();
    reloadData();
    setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
  };

  const handleTaskMenuDownload = async () => {
    setMenuLoading(true);
    try {
      if (selectedIds.length === 1) {
        // 单个任务：使用 itemName 作为文件名，并统一 CSV 扩展
        const task = selectedTasks[0];
        const base = (task?.itemName || '').trim();
        const suggestedName = base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`;
        await handleDownload(selectedIds[0], suggestedName);
      } else {
        // 多个任务：打包为 ZIP 一次下载，避免浏览器拦截多文件下载
        const zip = new JSZip();
        const extractionApi = await getApi(GlossaryExtractionApi);
        const usedNames = new Set<string>();

        const sanitize = (s: string) => s.replace(/[\\/:*?"<>|]/g, '_').replace(/[\r\n\t]/g, '').trim();
        const makeUnique = (name: string) => {
          if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
          }
          let i = 1;
          let candidate = `${name} (${i})`;
          while (usedNames.has(candidate)) {
            i += 1;
            candidate = `${name} (${i})`;
          }
          usedNames.add(candidate);
          return candidate;
        };

        for (const id of selectedIds) {
          try {
            const { data } = await extractionApi.getGlossaryExtractionDownloadLink(id);
            const url = data.downloadLink;
            const resp = await fetch(url);
            const blob = await resp.blob();
            // 统一命名：使用对应任务的 itemName 并强制使用 .csv 扩展
            const suggestedBase = (selectedTasks.find((item) => item.id === id)?.itemName || id).trim();
            let base = sanitize(suggestedBase || id);
            base = base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`;
            base = makeUnique(base);

            zip.file(base, blob);
          } catch (e) {
            console.error('Failed to add extraction file to zip:', e);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(zipBlob);
        a.href = objectUrl;
        a.download = 'glossary-extractions.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMenuLoading(false);
      handleMenuClose();
    }
  };

  const handleTaskMenuImport = async () => {
    if (selectedIds.length !== 1) {
      return;
    }
    setMenuLoading(true);
    try {
      const [id] = selectedIds;
      await handleDirectImport(id);
    } catch (error) {
      console.error(error);
    } finally {
      setMenuLoading(false);
    }
    handleMenuClose();
  };

  const handleTaskDownload = async (id: string, name?: string) => {
    setDownloadId(id);
    try {
      const base = (name || '').trim();
      const suggestedName = base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`;
      await handleDownload(id, suggestedName);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadId('');
    }
  };

  const handleDirectImport = async (id: string) => {
    setImportId(id);
    try {
      const result = await getGlossaryExtractionFile(id);
      if (result) {
        const { file, fileName } = result;
        // 使用state传递文件数据到ImportGlossary组件
        navigate('/glossary-management/import', {
          state: {
            extractedFile: file,
            extractedFileName: fileName,
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setImportId('');
    }
  };

  const handleRowDelete = async (id: string) => {
    if (menuLoading) return;
    setPendingDeleteIds([id]);
    setConfirmDeleteOpen(true);
  };

  const debouncedSetSearch = useCallback(debounce(setSearch, 500), [setSearch]);

  useEffect(() => {
    debouncedSetSearch(tempSearch);
  }, [tempSearch, debouncedSetSearch]);

  const selectedTasks = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) {
      return [];
    }
    return (data?.data.data ?? []).filter((item) => selectedIds.includes(item.id));
  }, [selectedIds, data]);

  const pendingDeleteNames = useMemo(() => {
    if (!pendingDeleteIds || pendingDeleteIds.length === 0) return [] as string[];
    return (
      data?.data.data
        .filter((item) => pendingDeleteIds.includes(item.id))
        .map((item) => item.itemName ?? '') ?? []
    );
  }, [pendingDeleteIds, data]);

  const allSelectedAreSuccess = useMemo(() => {
    return (
      selectedTasks.length > 0 &&
      selectedTasks.every(
        (task) => task.status === GlossaryExtractionResponseItemDtoStatusEnum.Success,
      )
    );
  }, [selectedTasks]);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('glossaryExtractionTasks.navigation.home')}</Link>
        <Typography color="textPrimary">
          {t('glossaryExtractionTasks.navigation.glossaryExtractionTasks')}
          </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            label={t('glossaryExtractionTasks.search.label')}
            variant="outlined"
            size="small"
            value={tempSearch}
            InputLabelProps={{
              shrink: true,
            }}
            // placeholder={t('glossaryExtractionTasks.search.placeholder')}
            onChange={(e) => {
              setTempSearch(e.target.value);
              setPage(0);
            }}
          />
          <Box>
            <Button
              sx={{ mr: 1 }}
              aria-label="refresh"
              variant="outlined"
              onClick={reloadData}
            >
              <RefreshIcon />
            </Button>
            <LoadingButton
              ref={actionsButtonRef}
              onClick={handleMenuOpen}
              endIcon={<ArrowDropDownIcon />}
              variant="outlined"
              disabled={selectedIds.length === 0 || menuLoading}
              loading={menuLoading}
            >
              {t('glossaryExtractionTasks.actions.button')}
            </LoadingButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  style: {
                    minWidth: actionsButtonRef.current?.offsetWidth,
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleTaskMenuDownload}
                disabled={!allSelectedAreSuccess || menuLoading}
              >
                <Typography>{t('glossaryExtractionTasks.actions.download')}</Typography>
              </MenuItem>
              <MenuItem
                onClick={handleTaskMenuImport}
                disabled={
                  selectedIds.length !== 1 ||
                  !allSelectedAreSuccess ||
                  menuLoading
                }
              >
                <Typography>
                  {t('glossaryExtractionTasks.actions.import')}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={handleDelete}
                disabled={selectedIds.length === 0 || menuLoading}
              >
                <Typography color="error">
                  {t('glossaryExtractionTasks.actions.delete')}
                </Typography>
              </MenuItem>
            </Menu>
            <Button
              component={Link}
              to="/extraction/glossary"
              variant="contained"
              color="primary"
              sx={{ ml: 1 }}
            >
              {t('glossaryExtractionTasks.actions.new')}
            </Button>
          </Box>
        </Box>
        <PaginatedTable
          totalCount={data?.data.metadata.totalCount}
          columns={[
            {
              id: 'itemName',
              label: t('glossaryExtractionTasks.table.name'),
              render: (record) => (
                <EllipsisCell content={(record as any).itemName ?? ''} maxLines={3} />
              ),
              width: 200,
            },
            {
              id: 'description',
              label: t('glossaryExtractionTasks.table.description'),
              render: (record) => (
                <EllipsisCell content={(record as any).description ?? ''} maxLines={3} />
              ),
            },
            {
              id: 'createdAt',
              label: t('glossaryExtractionTasks.table.createdAt'),
              render: ({ createdAt }) => formatDate(createdAt),
            },
            {
              id: 'updatedAt',
              label: t('glossaryExtractionTasks.table.updatedAt'),
              render: ({ updatedAt }) => formatDate(updatedAt),
            },
            {
              id: 'status',
              label: t('glossaryExtractionTasks.table.status'),
              render: ({ status, errorMessage }) => {
                const content = (
                  <Typography
                    variant="body2"
                    className={statusClassMapping[status]}
                  >
                    {statusIconMapping[status]}
                    <span className="align-middle ml-1">
                      {t(statusStringMapping[status])}
                    </span>
                  </Typography>
                );
                if (errorMessage) {
                  return (
                    <Tooltip title={errorMessage} placement="top" arrow>
                      {content}
                    </Tooltip>
                  );
                }
                return content;
              },
            },
            {
              id: 'actions',
              label: t('glossaryExtractionTasks.table.actions'),
              render: (record) => (
                <>
                  <Tooltip title={t('table.tooltip.download')} placement="top">
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        const baseName = (record.itemName || '').trim();
                        handleTaskDownload(record.id, baseName);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={record.status !== 'SUCCESS' || record.id === downloadId}
                    >
                      <FileDownloadOutlined/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('table.tooltip.import')} placement="top">
                    <IconButton
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); handleDirectImport(record.id); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={record.status !== 'SUCCESS' || record.id === importId}
                    >
                      <CloudUploadOutlinedIcon/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('table.tooltip.delete')} placement="top">
                    <IconButton
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); handleRowDelete(record.id); }}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={menuLoading}
                    >
                      <DeleteOutlineOutlined/>
                    </IconButton>
                  </Tooltip>
                </>
              ),
            }
          ]}
          data={data?.data.data ?? []}
          selectedItems={selectedIds}
          page={page}
          rowsPerPage={rowsPerPage}
          loading={isLoadingData}
          getItemId={(item) => item.id}
          onItemClick={(uuid) => {
            setSelectedIds((prev) =>
              prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
            );
          }}
          onSelectAllClick={(_e) => {
            const currentPageIds = (data?.data.data ?? []).map((item) => item.id);
            const allSelected = currentPageIds.every((id) => selectedIds.includes(id));
            setSelectedIds((prev) => {
              if (allSelected) {
                // 取消选择当前页所有项
                return prev.filter((id) => !currentPageIds.includes(id));
              } else {
                // 选择当前页所有项
                const set = new Set([...prev, ...currentPageIds]);
                return Array.from(set);
              }
            });
          }}
          onPageChange={(_, newPage) => {
            setPage(newPage);
            setSelectedIds([]);
          }}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
            setSelectedIds([]);
          }}
        />
        {/* 删除确认弹窗 */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>{t('glossaryExtractionTasks.actions.delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('translateTasks.confirmDelete.message', { count: pendingDeleteIds.length })}
            </DialogContentText>
            {pendingDeleteNames.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {pendingDeleteNames.join('、')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              loading={menuLoading}
            >
              {t('glossaryExtractionTasks.actions.delete')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </PaperCard>
    </Container>
  );
};
